import http from '@frontend/api/http';
import { HYPATIA_URL } from '@frontend/envConfig';
import type { AgentVisibility, ResourcePermissions } from '@frontend/types/agent';

import { AgentSortField, AgentStatus, AgentType, SortOrder } from '../hooks/useAgentsUrlParams';

export type UnifiedAgent = {
  id: string;
  agent_id: string;
  name: string;
  description: string;
  version: string;
  agent_type: 'specialized' | 'workflow';
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  is_active: boolean;
  is_deleted: boolean;
  is_installed: boolean;
  is_system_agent?: boolean;
  conversation_mode?: boolean;
  permissions?: ResourcePermissions;
  links: {
    self: string;
    collection: string;
    specialized_detail?: string;
    workflow_detail?: string;
    execute?: string;
    executions: string;
  };
};

export type AgentsApiResponse = {
  items: UnifiedAgent[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  links: {
    self: string;
    next?: string;
    previous?: string;
  };
};

export type GetAgentsParams = {
  instanceId: string;
  page?: number;
  pageSize?: number;
  searchQuery?: string;
  agentType?: AgentType;
  status?: AgentStatus;
  sort?: AgentSortField;
  order?: SortOrder;
  createdBy?: string[];
  updatedBy?: string[];
  visibility?: AgentVisibility;
};

export function getAgentsWithPagination({
  instanceId,
  page = 1,
  pageSize = 20,
  searchQuery = '',
  agentType,
  status,
  sort,
  order,
  createdBy,
  updatedBy,
  visibility,
}: GetAgentsParams) {
  return {
    key: [
      'agents',
      instanceId,
      page,
      pageSize,
      searchQuery,
      agentType,
      status,
      sort,
      order,
      createdBy,
      updatedBy,
      visibility,
    ] as const,
    queryFn: async (): Promise<AgentsApiResponse> => {
      // The UI uses the literal 'optimizely' inside `createdBy`/`updatedBy` to
      // mean "agents installed from the Optimizely Agent Directory". Split it
      // back out into the dedicated `include_system_agents` query param.
      const includeSystemAgents =
        !!createdBy?.includes('optimizely') || !!updatedBy?.includes('optimizely');
      const creatorIds = createdBy?.filter(id => id !== 'optimizely') ?? [];
      const updaterIds = updatedBy?.filter(id => id !== 'optimizely') ?? [];

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        status: status || 'all',
        ...(searchQuery && { search_query: searchQuery }),
        ...(agentType && { agent_type: agentType }),
        ...(sort && { sort }),
        ...(order && { order }),
        ...(creatorIds.length && { created_by: creatorIds.join(',') }),
        ...(includeSystemAgents && { include_system_agents: 'true' }),
        ...(updaterIds.length && { updated_by: updaterIds.join(',') }),
        ...(visibility && { visibility }),
      });

      const res = await http.get<AgentsApiResponse>(
        `${HYPATIA_URL}/api/v1/${instanceId}/agents?${params}`
      );
      return res.data;
    },
  };
}

export function getAgentById(instanceId: string, agentId: string) {
  return {
    key: ['agent', instanceId, agentId] as const,
    queryFn: async (): Promise<UnifiedAgent> => {
      const res = await http.get<UnifiedAgent>(
        `${HYPATIA_URL}/api/v1/${instanceId}/agents/${agentId}`
      );
      return res.data;
    },
  };
}
